import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuItem } from "@/components/ui/sidebar";
import SidebarSvg from "@/assets/home/sidebar.svg?react";
import type { Record } from "@/types";
import add from "@/assets/search/add.png";
import { useEffect, useState, useRef } from "react";
import "./index.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import eventBus from "@/utils/eventBus";
import { envStorage } from "@/utils/envCache";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { EllipsisVertical, Inbox, PanelLeft } from "lucide-react";
import Loading from "@/icons/three-dots.svg?react";
import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from "@/components/ui/menubar";
import Edit from "@/assets/search/edit.svg?react";
import Delete from "@/assets/search/delete.svg?react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ExclamationCircleIcon } from "@heroicons/react/16/solid";
import useCounterStore from "@/store";
import { HoverTip } from "@/components/hoverTip";
import { useTranslation } from "@/locales";
import AuthService from "@/services/auth";
import { useChatHistory } from "@/hooks/useChatHistory";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import setImg from "@/assets/home/set.png";
import outImg from "@/assets/home/out.png";
import { useGlobalStore } from "@/store/store.ts";
import { getDefaultBrandConfigForHost, getSavedSourceSystemConfig } from "@/config/sourceSystem";
import { BrandLogo, BrandTitle } from "@/components/SourceSystemBrand";

interface NavItem {
  title: string;
  url: string;
  icon: any;
  id: string;
}

// 完全控制菜单项
const FULL_CONTROL_ITEMS: NavItem[] = [
  {
    title: "admin.teamManagement",
    url: "/admin/teamUser",
    icon: Inbox,
    id: "teamUser",
  },
  {
    title: "admin.fileCollection",
    url: "/admin/fileData",
    icon: Inbox,
    id: "fileData",
  },
  {
    title: "admin.historicalIssues",
    url: "/admin/HistoricalIssues",
    icon: Inbox,
    id: "HistoricalIssues",
  },
];

// 托管团队菜单项
const HOSTED_ITEMS: NavItem[] = [
  {
    title: "admin.teamUser",
    url: "/admin/teamUser",
    icon: Inbox,
    id: "teamUser",
  },
  {
    title: "admin.historicalIssues",
    url: "/admin/HistoricalIssues",
    icon: Inbox,
    id: "HistoricalIssues",
  },
];

interface AppSidebarProps {
  onTitleEdit?: (title: string, topicNo: string) => void;
  onNavItemClick?: () => void;
}

export function AppSidebar({ onTitleEdit, onNavItemClick }: AppSidebarProps) {
  const { t } = useTranslation();
  const open = useCounterStore((state) => state.open);
  const navigate = useNavigate();
  const { paramsId } = useParams<{ paramsId: string }>();
  const location = useLocation();
  const currentPath = location.pathname;
  const isAdminPath = currentPath.includes("/admin");
  const brandConfig = isAdminPath ? getDefaultBrandConfigForHost() : getSavedSourceSystemConfig();
  const isMobile = useIsMobile();
  const { chatRecord, isLoading, hasMore, loadMore, deleteTopic } = useChatHistory();

  // Admin related state
  const [admin, setAdmin] = useState(false);
  const [adminFlag, setAdminFlag] = useState("");
  const [itemsAdmin, setItemsAdmin] = useState<NavItem[]>([]);

  // UI interaction state
  const [mouseIndex, setMouseIndex] = useState<number | null>(null);
  const [delDialogOpen, setDelDialogOpen] = useState(false);
  const [delTarget, setDelTarget] = useState<Record | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentPath.includes("/admin")) {
      setAdmin(true);
      const adminRouterId: string = envStorage.getItem("adminRouterId") || "teamUser";
      setAdminFlag(adminRouterId);
      
      // 初始化 admin 菜单项
      const isFULL_CONTROL = envStorage.getItem("isFULL_CONTROL");
      const items = isFULL_CONTROL === "FULL_CONTROL" ? FULL_CONTROL_ITEMS : HOSTED_ITEMS;
      setItemsAdmin(items.map(item => ({
        ...item,
        title: t(item.title)
      })));
    } else {
      setAdmin(false);
    }
  }, [currentPath, t]);

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !isLoading) {
        loadMore();
      }
    }
  };

  const handleLoad = () => {
    onNavItemClick?.(); // 移动端点击跳转到历史聊天，关闭侧边栏
    navigate("/");
    eventBus.emit("load");
  };

  const toHistory = (item: Record) => {
    navigate(`/${item.topicNo}`);
    eventBus.emit("changeTitle", item.topicName);
    eventBus.emit("recordChange");
    onNavItemClick?.(); // 移动端点击跳转到历史聊天，关闭侧边栏
  };

  const onEdit = (item: Record) => {
    onTitleEdit?.(item.topicName, item.topicNo);
  }

  const onDel = (item: Record) => {
    setDelTarget(item);
    setDelDialogOpen(true);
  }

  const handleSettings = () => {
    navigate("/settings");
    onNavItemClick?.();
  };

  const handleLogout = () => {
    //退出登录
    AuthService.clearTokens();
    navigate("/", { replace: true });
    window.location.reload();
    return false;
  };

  const handleSkip = (item: NavItem) => {
    // 检查是否有文件正在上传
    if (localStorage.getItem("isUploading") === "true") {
      toast.info(t("admin.fileUploadingPleaseWait"));
      return;
    }

    // 如果没有文件正在上传，直接跳转
    setAdminFlag(item.id);
    envStorage.setItem("adminRouterId", item.id);
    navigate(item.url);
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    const success = await deleteTopic(delTarget.topicNo);
    if (success) {
      toast.success(t("status.deleteSuccess"));
      if (delTarget.topicNo === paramsId) {
        handleLoad();
      }
    }
    setDelDialogOpen(false);
    setDelTarget(null);
  };

  const userInfo = useGlobalStore((state) => state.user);

  return (
    <>
      <Sidebar>
        <SidebarContent className={"bg-[var(--background-left_navigation_web)] border-none overflow-hidden flex flex-col h-full"}>
          <SidebarGroup className="flex flex-col flex-1 min-h-0">
            <SidebarGroupLabel className="">
              <a className={"w-full z-40 h-[56px]"}>
                <div className="flex items-center justify-between pt-[24px]">
                  <div className="flex flex-col">
                    <div className="flex items-center cursor-pointer">
                      <BrandLogo config={brandConfig} className="w-[32px] h-[32px]" />
                      <div className="h-[32px]">
                        <BrandTitle config={brandConfig} className="inline-block max-w-[150px] truncate font-extrabold ml-2 text-[14px] leading-[16px]" />
                        <br />
                        {userInfo?.teamName && <span className=" ml-2 text-[10px] leading-[10px] text-[#6A6A60]">{userInfo.teamName}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {
                      isMobile ? (
                        <Button variant="ghost" size="icon" onClick={() => onNavItemClick?.()}>
                          <PanelLeft className="w-3 h-5 text-[var(--text-text_primary)]" />
                        </Button>
                      ) : (
                        <HoverTip position={"bottom"} content={t("admin.collapseSidebar")}>
                          <SidebarSvg className="w-[18px] h-[18px] text-[20px] cursor-pointer fill-[var(--text-text_primary)]" onClick={() => useCounterStore.getState().setOpen(!open)} />
                        </HoverTip>
                      )
                    }
                  </div>
                </div>
              </a>
            </SidebarGroupLabel>
            {!admin && (
              <SidebarGroupContent className="flex-1 flex flex-col min-h-0">
                <SidebarMenu>
                  <SidebarMenuItem className="mt-8 mb-4">
                    <a onClick={handleLoad}>
                      <div className="sidebar-add flex items-center py-2 cursor-pointer">
                        <img src={add} className="w-[16px] h-[16px] " alt="" />
                        <div className="text-white ml-2">{t("chat.newChat")}</div>
                      </div>
                    </a>
                  </SidebarMenuItem>
                  <div className="line"></div>
                </SidebarMenu>
                <SidebarMenu className="sidebar-menu-scrollable">
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-none px-2 py-1">
                      <span>{t("admin.chat")}</span>
                    </div>
                    {
                      isMobile ? (
                        <div
                          ref={listRef}
                          className="overflow-y-auto record-list flex-1"
                          onScroll={handleScroll}
                          style={{ minHeight: 0, height: 0 }}
                        >
                          {chatRecord.content.map((item) => (
                            <div
                              key={item.topicNo}
                              onClick={() => toHistory(item)}
                              className={item.topicNo == paramsId ? "is-topic-active is-topic" : "is-topic"}
                            >
                              <div className={"flex items-center justify-between"}>
                                <div className="truncate-1-lines ">{item.topicName ?? t("chat.newChat")}</div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button onClick={(e) => e.stopPropagation()} className="p-1 pr-1 -mr-2 outline-none focus:outline-none focus-visible:outline-none">
                                      <EllipsisVertical onClick={(e) => e.stopPropagation()} style={{ fontSize: "14px", color: "var(--text-text_primary)", zoom: "0.6" }} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenuItem onClick={() => onEdit(item)}>
                                      <Edit />
                                      <span>{t("common.editName")}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => onDel(item)} className="text-red-500 focus:text-red-500">
                                      <Delete />
                                      <span>{t("common.delete")}</span>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                          {isLoading && (
                            <div className="flex justify-center items-center py-4">
                              <Loading />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          ref={listRef}
                          className="overflow-y-auto record-list flex-1"
                          onScroll={handleScroll}
                          style={{ minHeight: 0, height: 0 }}
                        >
                          {chatRecord.content.map((item, index) => (
                            <div
                              key={item.topicNo}
                              onClick={() => toHistory(item)}
                              onMouseLeave={() => setMouseIndex(null)}
                              onMouseEnter={() => setMouseIndex(index)}
                              className={item.topicNo == paramsId ? "is-topic-active is-topic" : "is-topic"}
                            >
                              <div className={"flex items-center justify-between"}>
                                <HoverCard>
                                  <HoverCardTrigger asChild>
                                    <div className="truncate-1-lines ">{item.topicName ?? t("chat.newChat")}</div>
                                  </HoverCardTrigger>
                                  <HoverCardContent side={"right"} className="w-auto">
                                    <div className="text-[14px]">{item.topicName ?? t("chat.newChat")}</div>
                                  </HoverCardContent>
                                </HoverCard>
                                {(mouseIndex === index || item.topicNo == paramsId) && (
                                  <Menubar style={{ border: "none", boxShadow: "none", padding: "0", background: "none", height: "auto" }}>
                                    <MenubarMenu>
                                      <MenubarTrigger className={"p-0"} style={{ background: "none" }} onClick={(e) => e.stopPropagation()}>
                                        <EllipsisVertical style={{ fontSize: "14px", color: "var(--text-text_primary)", zoom: "0.6" }} />
                                      </MenubarTrigger>
                                      <MenubarContent className="min-w-[7rem]">
                                        <MenubarItem onClick={(e) => { e.stopPropagation(); onEdit(item); }}>
                                          <Edit />
                                          {t("common.editName")}
                                        </MenubarItem>
                                        <MenubarItem onClick={(e) => { e.stopPropagation(); onDel(item); }}>
                                          <Delete />
                                          <span className={"text-red-500"}>{t("common.delete")}</span>
                                        </MenubarItem>
                                      </MenubarContent>
                                    </MenubarMenu>
                                  </Menubar>
                                )}
                              </div>
                            </div>
                          ))}
                          {isLoading && (
                            <div className="flex justify-center items-center py-4">
                              <Loading />
                            </div>
                          )}
                        </div>
                      )
                    }

                  </div>
                </SidebarMenu>
              </SidebarGroupContent>
            )}
            {admin && (
              <SidebarGroupContent
                className="flex-1"
                style={{
                  marginTop: "26px",
                  borderTop: "1px solid #ddddde",
                  paddingTop: "20px",
                }}
              >
                <SidebarMenu>
                  <div className="flex-1 flex flex-col">
                    <div className={`overflow-auto record-list`}>
                      {itemsAdmin.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleSkip(item)}
                          className={item.id == adminFlag ? "is-topic-active-admin dark:hover:bg-[#303030] hover:bg-[#e7e7e7]" : "is-topic-admin dark:hover:bg-[#303030] hover:bg-[#e7e7e7]"}
                        >
                          <div className={"flex items-center justify-between"}>
                            <div className="truncate-1-lines ">{item.title}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>

          {isMobile && userInfo && (
            <div className="mt-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-full justify-start items-center text-left h-auto pt-0">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback style={{ backgroundColor: "#0169cc", color: "#fff" }}>
                        {userInfo.nickname.slice(0, 1)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate font-medium">{userInfo.nickname}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="center" className="w-[calc(50vw-1rem)] mb-2">
                   <DropdownMenuItem onClick={handleSettings} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))] focus:bg-[hsl(var(--destructive))]/10">
                     <img src={setImg} alt="" />
                     {t("common.settings")}
                   </DropdownMenuItem>
                   {AuthService.getAccountType() !== 'OPEN' && (
                   <DropdownMenuItem onClick={handleLogout} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))] focus:bg-[hsl(var(--destructive))]/10">
                     <img src={outImg} alt="" />
                     {t("auth.logout")}
                 </DropdownMenuItem>
                   )}
                 </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}  
        </SidebarContent>
      </Sidebar>
      <AlertDialog open={delDialogOpen} onOpenChange={setDelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={"flex items-start"}>
              <ExclamationCircleIcon className={"w-[22px] mr-[8px] text-red-500"} /> {t("common.confirmDelete")}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("common.deleteConversationWarning")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row gap-2 md:justify-end">
            <AlertDialogCancel
              onClick={() => setDelDialogOpen(false)}
              className="flex-1 md:flex-none"
            >
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="flex-1 md:flex-none bg-red-500 hover:bg-red-600"
              onClick={confirmDelete}
            >
              {t("common.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
